import { isoDateFromMs } from '../time/moscowCalendar';
import { dailyLoginPassRewardForStreakDay, PASSES_CAP } from './constants';
import type { PlayerEconomyState } from './types';

const DAY_MS = 24 * 60 * 60 * 1000;

function dayDiff(fromIso: string, toIso: string): number | null {
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  if (!Number.isFinite(from) || !Number.isFinite(to)) {
    return null;
  }
  return Math.floor((to - from) / DAY_MS);
}

export type DailyLoginClaimResult = {
  alreadyClaimed: boolean;
  state: PlayerEconomyState;
  rewardPasses: number;
};

export function isDailyLoginRewardClaimedToday(state: PlayerEconomyState, nowMs = Date.now()): boolean {
  const today = isoDateFromMs(nowMs);
  return state.lastDailyRewardClaimedDate === today;
}

export function isDailyLoginStreakCompleted(state: PlayerEconomyState): boolean {
  return state.dailyLoginStreakCompleted === true;
}

export function getNextDailyLoginReward(state: PlayerEconomyState, nowMs = Date.now()): number {
  if (isDailyLoginStreakCompleted(state)) {
    return dailyLoginPassRewardForStreakDay(7);
  }
  const today = isoDateFromMs(nowMs);
  const lastClaim = state.lastDailyRewardClaimedDate ?? null;
  if (lastClaim === today) {
    return dailyLoginPassRewardForStreakDay(state.streakDays);
  }
  if (lastClaim == null) {
    return dailyLoginPassRewardForStreakDay(1);
  }
  const diff = dayDiff(lastClaim, today);
  if (diff == null || diff > 1) {
    return dailyLoginPassRewardForStreakDay(1);
  }
  if (diff === 1) {
    return dailyLoginPassRewardForStreakDay(Math.min(7, state.streakDays + 1));
  }
  return dailyLoginPassRewardForStreakDay(1);
}

/** Для dev/тестов: серия из N дней, последняя награда забрана вчера. */
export function buildDailyLoginStateWithStreak(
  state: PlayerEconomyState,
  streakDays: number,
  nowMs = Date.now(),
): PlayerEconomyState {
  const yesterday = isoDateFromMs(nowMs - DAY_MS);
  const streak = Math.max(0, Math.min(7, Math.floor(streakDays)));
  return {
    ...state,
    streakDays: streak,
    lastLoginDate: yesterday,
    lastDailyRewardClaimedDate: yesterday,
  };
}

export function claimDailyLoginReward(state: PlayerEconomyState, nowMs = Date.now()): DailyLoginClaimResult {
  if (isDailyLoginStreakCompleted(state)) {
    return { alreadyClaimed: true, state, rewardPasses: 0 };
  }

  const today = isoDateFromMs(nowMs);
  if (state.lastDailyRewardClaimedDate === today) {
    return { alreadyClaimed: true, state, rewardPasses: 0 };
  }

  const lastClaim = state.lastDailyRewardClaimedDate ?? null;
  let newStreak: number;
  if (lastClaim == null) {
    newStreak = 1;
  } else {
    const diff = dayDiff(lastClaim, today);
    if (diff === 1) {
      newStreak = Math.min(7, state.streakDays + 1);
    } else {
      newStreak = 1;
    }
  }

  const rewardPasses = dailyLoginPassRewardForStreakDay(newStreak);
  const passesBalance = Math.min(PASSES_CAP, state.passesBalance + rewardPasses);

  return {
    alreadyClaimed: false,
    rewardPasses,
    state: {
      ...state,
      streakDays: newStreak,
      lastLoginDate: today,
      lastDailyRewardClaimedDate: today,
      dailyLoginStreakCompleted: newStreak >= 7 ? true : state.dailyLoginStreakCompleted,
      passesBalance,
    },
  };
}
